import React, { useEffect, useRef, useMemo, useState, useCallback } from 'react';
import { X, Search, BookOpen } from 'lucide-react';
import {
  EDX_COLORS,            // [band1..band9] hex array — the only literal-hex exception
  EDX_LABELS,            // ['Collapsed'..'Flourishing']
  BRITTLENESS_CONTEXT,   // Non-brittle / Semi-brittle / Brittle reference copy
} from '../constants/edxDescriptions';
import { TERM_HELP_GLOSSARY } from '../constants/termHelpGlossary';
import HowToReadScores from './help/HowToReadScores';
import './HelpDrawer.css';

/* ------------------------------------------------------------------ *
 * EDX 1-9 LADDER (rendered 9 -> 1). Names + colour chips are derived
 * from the canonical EDX_LABELS + EDX_COLORS so they never drift from the
 * pipeline banding. The one-liners (handoff 09 §A5) are curated so every
 * band reads at the same altitude, instead of slicing the first clause of
 * each band description (which gave uneven gists).
 * ------------------------------------------------------------------ */
const EDX_LADDER_DESC = {
  9: 'Builds capacity through disturbance',
  8: 'Strong function; regenerative target',
  7: 'Solid function; sustained productivity',
  6: 'Cycles working but patchy',
  5: 'Improving; resilience still limited',
  4: 'Function faltering; intervention point',
  3: 'Cycles impaired; erosion common',
  2: 'Low function; active erosion',
  1: 'Severe loss; near-bare ground',
};
const EDX_LADDER = [9, 8, 7, 6, 5, 4, 3, 2, 1].map((band) => ({
  band,
  name: EDX_LABELS[band - 1],
  color: EDX_COLORS[band - 1],
  desc: EDX_LADDER_DESC[band],
}));

/* ------------------------------------------------------------------ *
 * BRITTLENESS reference gradient (low -> high). Stops map to the EDX
 * green->amber->rust family; copy is the canonical BRITTLENESS_CONTEXT.
 * ------------------------------------------------------------------ */
const BRITTLENESS_REF = {
  // green (humid, reliable decay) -> rust (arid, decay fails)
  gradient: `linear-gradient(90deg, ${EDX_COLORS[7]} 0%, ${EDX_COLORS[4]} 50%, ${EDX_COLORS[1]} 100%)`,
  stops: [
    { label: BRITTLENESS_CONTEXT.low.label, range: '1\u20133', desc: BRITTLENESS_CONTEXT.low.description },
    { label: BRITTLENESS_CONTEXT.moderate.label, range: '4\u20136', desc: BRITTLENESS_CONTEXT.moderate.description },
    { label: BRITTLENESS_CONTEXT.high.label, range: '7\u201310', desc: BRITTLENESS_CONTEXT.high.description },
  ],
};

/* ------------------------------------------------------------------ *
 * HELP_GLOSSARY (handoff 09 §A4) — the canonical LHR "Other Terms" A-Z
 * list (45 entries) from the ReportBuilder report template
 * (generate_report.py -> _build_appendix_glossary -> _glossary_terms).
 * The ReportBuilder template is the source of truth for these wordings.
 * Rendered as a single category so the existing filtered.map(cat => ...)
 * render is unchanged. Voice: no em dashes. (DEM resolved for UK; swap per
 * jurisdiction if a _dem_source is available.)
 * ------------------------------------------------------------------ */
export const HELP_GLOSSARY = [
  { id: 'glossary', title: 'Glossary (A\u2013Z)', terms: [
    { term: 'AGB', def: 'Above-Ground Biomass, standing plant biomass above the soil surface, estimated from PALSAR-2 L-band radar (tC/ha)' },
    { term: 'Baseline', def: 'Disclosure status: a baseline is assembled and liftable, not yet evidenced' },
    { term: 'BNG', def: 'Biodiversity Net Gain. UK planning requirement for measurable habitat improvement' },
    { term: 'BSI', def: 'Bare Soil Index, spectral measure of exposed soil' },
    { term: 'Catena', def: 'Soil sequence from ridge to valley' },
    { term: 'CDI', def: 'Community Dynamics Index, structural diversity and vegetation complexity' },
    { term: 'CIred', def: 'Chlorophyll Index Red-edge, canopy chlorophyll concentration derived from Sentinel-2 red-edge bands' },
    { term: 'CV', def: 'Coefficient of Variation, standard deviation divided by mean, a measure of relative variability' },
    { term: 'DEM', def: 'Digital Elevation Model, gridded terrain height data (UK LIDAR 2m used in this report)' },
    { term: 'Disclosure-ready', def: "EcoIntel's disclosure ceiling: every framework at Evidenced or Documented = 100%, reachable with zero VVB" },
    { term: 'Documented', def: 'Disclosure status: operator-attested with evidence on file. Counts toward assessed, never promoted to Evidenced' },
    { term: 'Dynamic World', def: 'Google Dynamic World, near-real-time 10m land cover classification from Sentinel-2' },
    { term: 'ECOSTRESS', def: 'ECOsystem Spaceborne Thermal Radiometer on the International Space Station, high-resolution thermal imaging' },
    { term: 'EDX', def: 'EcoDynamic Index: the 1 to 9 Land Health Score and band name' },
    { term: 'EFI', def: 'Energy Flow Index, photosynthetic capacity and biomass production' },
    { term: 'EOV', def: 'Ecological Outcome Verification. Savory Institute field assessment protocol for measuring ecological health outcomes' },
    { term: 'ERA5', def: 'ECMWF Reanalysis v5, global climate dataset' },
    { term: 'ESRS', def: 'European Sustainability Reporting Standards. EU mandatory disclosure framework (E1 = climate, E4 = biodiversity)' },
    { term: 'ET/PET', def: 'Actual/Potential Evapotranspiration ratio' },
    { term: 'EVI', def: 'Enhanced Vegetation Index, improved greenness measure that reduces atmospheric and soil background effects' },
    { term: 'Evidenced', def: 'Disclosure status: backed by verifiable, satellite-derived evidence for this property' },
    { term: 'GHG Protocol', def: 'Greenhouse Gas Protocol, international corporate standard for greenhouse gas accounting' },
    { term: 'GPP', def: 'Gross Primary Productivity, total photosynthetic carbon capture (gC/m\u00b2/yr)' },
    { term: 'GS', def: 'Growing Season, the period of active vegetation growth, typically April\u2013October in UK temperate climates' },
    { term: 'GT / GT*', def: 'GT = Ground Truth (in-field ecological assessment score). GT* = weather-corrected ground truth, removing assessor bias caused by weather conditions on the day of assessment' },
    { term: 'HV / VH / VV', def: 'Radar polarisation modes. VH = vertical-horizontal (Sentinel-1, biomass proxy). HV = horizontal-vertical (PALSAR-2, woody biomass). VV = vertical-vertical (Sentinel-1, moisture/roughness)' },
    { term: 'ISO 14064-2', def: 'International standard for quantification and reporting of greenhouse gas emission reductions at project level' },
    { term: 'Lift', def: 'Records a management representation + evidence to raise a Baseline framework to Documented. Never recolours satellite evidence to Evidenced, never itself verifies' },
    { term: 'LST', def: 'Land Surface Temperature, satellite-derived surface thermal emission, used for thermal environment characterisation' },
    { term: 'MCI', def: 'Mineral Cycling Index, nutrient turnover and soil stability' },
    { term: 'MOD17A2H', def: 'MODIS Gross Primary Productivity product, 8-day GPP estimates used for carbon flux calibration' },
    { term: 'MTCI', def: 'MERIS Terrestrial Chlorophyll Index, canopy chlorophyll content from Sentinel-2 red-edge reflectance' },
    { term: 'Net carbon uptake', def: 'Carbon balance after exports (tCO\u2082e/ha/yr)' },
    { term: 'NDMI', def: 'Normalised Difference Moisture Index, canopy and soil water content from Sentinel-2 shortwave infrared' },
    { term: 'NDVI', def: 'Normalised Difference Vegetation Index, greenness measure' },
    { term: 'NEE', def: 'Net Ecosystem Exchange. GPP minus respiration' },
    { term: 'Not assessed', def: 'Disclosure status: out of scope this cycle, stated honestly, never padded' },
    { term: 'PALSAR-2', def: 'JAXA ALOS-2 PALSAR-2. L-band Synthetic Aperture Radar satellite for woody biomass and structural estimation' },
    { term: 'Positioned', def: 'Disclosure status: mapped to a framework and TNFD-aligned, narrative-ready, but not yet evidenced' },
    { term: 'Reco', def: 'Ecosystem Respiration, carbon released through biological activity' },
    { term: 'RS', def: 'Remote Sensing, satellite-derived ecosystem scores, as distinct from ground-truth (GT) field assessments' },
    { term: 'Shannon H', def: 'Shannon Diversity Index, information-theoretic measure of spectral class diversity across the landscape' },
    { term: 'SMAP', def: 'NASA Soil Moisture Active Passive, satellite-derived surface soil moisture estimates' },
    { term: 'SOC', def: 'Soil Organic Carbon, carbon stored in soil organic matter, estimated from SoilGrids 250m baseline' },
    { term: 'SoilGrids', def: 'ISRIC SoilGrids 250m, global gridded database of soil properties including organic carbon, bulk density, and texture' },
    { term: 'TNFD', def: 'Taskforce on Nature-related Financial Disclosures, framework for organisations to report nature-related risks and opportunities' },
    { term: 'TPI', def: 'Topographic Position Index, landscape position (ridge/valley/flat)' },
    { term: 'TWI', def: 'Topographic Wetness Index, predicted soil moisture from terrain' },
    { term: 'VPD', def: 'Vapour Pressure Deficit, atmospheric dryness stress indicator' },
    { term: 'VVB', def: "Validation / Verification Body, an accredited external verifier. Optional, beyond EcoIntel's ceiling" },
    { term: 'WCI', def: 'Water Cycling Index, water infiltration and retention effectiveness' },
    { term: 'σ-corroboration', def: 'Cross-layer agreement check that threads through every carbon framework as the uncertainty / data-quality clause' },
  ]},
  // Handoff 58 §4 (folds the closed handoff 35). Plain-language FAQ. Keep the
  // indicative wording; do not upgrade the certainty.
  { id: 'cooling', title: 'Local cooling', terms: [
    { term: 'What is the Local Cooling Contribution?', def: 'It is how much cooler your land runs than comparable neighbouring land, read from satellite surface temperature on the same overpasses (so climate and season cancel out). We show it as a 1-to-9 band and a plain-language degrees figure ("about 2\u00b0C cooler on the hottest days"). It sits beside your Land Health Score and is never added into it.' },
    { term: 'How is it measured?', def: 'For each midday satellite pass from April to September that is clear of cloud over both your land and the land around it, your property\'s surface temperature is compared with a matched ring of surrounding land (trees, grass and crops 0.5 to 10 km around, matched for cover, elevation and distance to coast). The band comes from the hottest third of those passes, because that is when cooling matters. Land that pulls further ahead of its neighbours as the heat rises is buffering; land that falls behind is amplifying.' },
    { term: 'Why is it separate from the Land Health Score?', def: 'Cooling is a consequence of a working water cycle, so it belongs on the same 1-to-9 scale, but it is a separate, measured read from surface temperature rather than a score built from vegetation. Folding it into the Land Health Score would count the same thing twice. Read them together: your Land Health Score is the condition, your cooling band is one of its outcomes.' },
    { term: 'What is the "Evaporative cooling" score, and the per-hectare figures?', def: 'A second, separate measure: your land\'s own evaporative cooling, worked out from its own energy balance rather than by comparison with its neighbours. The per-hectare figures are the scale of water your land moves into the air and the cooling that comes with it each growing season, set mostly by your climate, not your management, so treat them as the size of the engine. They are not part of the Local Cooling Contribution band, and the two can differ.' },
    { term: 'Why does it say indicative?', def: 'It is a developing, honest read. We show a band wherever there are enough usable midday satellite passes over your land. Where your land is within 0.75\u00b0C of comparable land on the hottest days, inside the measurement\'s uncertainty, you will see band 5, "in line with comparable land," and no degrees figure; beyond that we show the band, the degrees figure and how the gap changes as the heat rises. Where we have no thermal record, or too few usable passes, it reads "not available." The comparison is always local and relative, never a planetary claim, and it is never added to your carbon figures.' },
  ]},
];

/* ------------------------------------------------------------------ *
 * DEFAULT_EDX_REFERENCE — the standard on-page reference block (EDX 1-9
 * ladder + brittleness gradient). Extracted to a node so callers can swap
 * it via the `referenceBlock` prop (handoff 10: the Disclosure page passes
 * the five-honest-statuses block instead). Every other page passes nothing
 * and keeps this default byte-for-byte.
 * ------------------------------------------------------------------ */
const DEFAULT_EDX_REFERENCE = (
  <>
    {/* EDX 1-9 ladder, 9 -> 1 */}
    <section className="help-block">
      <h4 className="help-block-title">Land Health bands</h4>
      <ul className="help-ladder">
        {EDX_LADDER.map((row) => (
          <li key={row.band} className="help-ladder-row">
            <span className="help-ladder-chip mono" style={{ backgroundColor: row.color }}>
              {row.band}
            </span>
            <span className="help-ladder-name">{row.name}</span>
            <span className="help-ladder-desc">{row.desc}</span>
          </li>
        ))}
      </ul>
    </section>

    {/* Brittleness reference gradient */}
    <section className="help-block">
      <h4 className="help-block-title">Brittleness scale</h4>
      <div className="help-britt-bar" style={{ background: BRITTLENESS_REF.gradient }}>
        <span className="help-britt-end help-britt-low">1</span>
        <span className="help-britt-end help-britt-high">10</span>
      </div>
      <div className="help-britt-stops">
        {BRITTLENESS_REF.stops.map((s) => (
          <div key={s.range} className="help-britt-stop">
            <span className="help-britt-label mono">{s.label} ({s.range})</span>
            <p>{s.desc}</p>
          </div>
        ))}
      </div>
    </section>
  </>
);

/* ------------------------------------------------------------------ *
 * Search helpers: case-insensitive substring match, escape the query
 * before building the highlight regex.
 * ------------------------------------------------------------------ */
const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

function Highlight({ text, query }) {
  if (!query) return text;
  const re = new RegExp(`(${escapeRe(query)})`, 'ig');
  const parts = String(text).split(re);
  return parts.map((part, i) =>
    re.test(part) && part.toLowerCase() === query.toLowerCase()
      ? <mark key={i} className="help-mark">{part}</mark>
      : <React.Fragment key={i}>{part}</React.Fragment>
  );
}

/* ------------------------------------------------------------------ *
 * HelpDrawer — controlled, right-anchored. Reuses the Modal overlay
 * recipe; the panel slides in from the right. Escape closes; the
 * search input is focused on open.
 * `contextNote` is the "On this page" string the page passes in.
 * When `siteContext` is present, widen the drawer and show the RB-UX
 * "How to read the scores" primer + tables (handoff 30 Item 2).
 * ------------------------------------------------------------------ */
export default function HelpDrawer({
  isOpen,
  onClose,
  lockOpen = false,
  onTakeTour,
  contextNote = 'Analytics & diagnostics for your property.',
  referenceBlock = DEFAULT_EDX_REFERENCE,
  siteContext = null,
  focusTermId = null,
  clearFocusTerm = () => {},
}) {
  const [query, setQuery] = useState('');
  const searchRef = useRef(null);
  const panelRef = useRef(null);
  const bodyRef = useRef(null);
  const wide = !!siteContext;

  const handleClose = useCallback(() => {
    if (lockOpen) return;
    onClose();
  }, [lockOpen, onClose]);

  // Focus the search box once the panel has mounted/animated in.
  // Skip when deep-linking to a term (handoff 30 Item 3).
  useEffect(() => {
    if (isOpen && !focusTermId) {
      setQuery('');
      const t = setTimeout(() => searchRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
    if (isOpen && focusTermId) setQuery('');
    return undefined;
  }, [isOpen, focusTermId]);

  // Escape closes (unless a product-tour glossary step has locked the drawer).
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, handleClose]);

  // Scroll to a glossary term after openHelpToTerm (handoff 30 Item 3).
  useEffect(() => {
    if (!isOpen || !focusTermId) return undefined;
    const t = setTimeout(() => {
      const el = document.getElementById(`gl-${focusTermId}`);
      if (el) {
        el.scrollIntoView({ block: 'start', behavior: 'smooth' });
        el.classList.remove('gl-flash');
        void el.offsetWidth;
        el.classList.add('gl-flash');
        setTimeout(() => el.classList.remove('gl-flash'), 1950);
      }
      clearFocusTerm();
    }, 210);
    return () => clearTimeout(t);
  }, [isOpen, focusTermId, clearFocusTerm]);

  const q = query.trim().toLowerCase();
  const searching = q.length > 0;

  // Filter glossary by term OR definition; keep only categories with hits.
  const filtered = useMemo(() => {
    if (!searching) return HELP_GLOSSARY;
    return HELP_GLOSSARY
      .map((cat) => ({
        ...cat,
        terms: cat.terms.filter(
          (t) => t.term.toLowerCase().includes(q) || t.def.toLowerCase().includes(q)
        ),
      }))
      .filter((cat) => cat.terms.length > 0);
  }, [q, searching]);

  const filteredTermHelp = useMemo(() => {
    if (!searching) return TERM_HELP_GLOSSARY;
    return TERM_HELP_GLOSSARY.filter(
      (t) => t.term.toLowerCase().includes(q)
        || t.short.toLowerCase().includes(q)
        || t.long.toLowerCase().includes(q)
    );
  }, [q, searching]);

  const hasResults = filtered.length > 0 || filteredTermHelp.length > 0;
  const stop = useCallback((e) => e.stopPropagation(), []);

  const scrollToId = useCallback((id) => {
    setQuery('');
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, []);

  if (!isOpen) return null;

  return (
    // Reused Modal recipe, re-anchored right. Backdrop click closes.
    <div
      className="help-drawer fixed inset-0 z-50 flex justify-end bg-black/50 backdrop-blur-sm animate-in fade-in duration-200"
      onMouseDown={handleClose}
      role="dialog"
      aria-modal="true"
      aria-label="Help and glossary"
    >
      <aside
        ref={panelRef}
        onMouseDown={stop}
        className={`help-drawer-panel h-full w-full flex flex-col animate-in slide-in-from-right duration-200 ${wide ? 'help-drawer-wide' : 'max-w-[420px]'}`}
      >
        {/* Deep-green header with eyebrow + scoped close control */}
        <header className="help-drawer-head">
          <div className="flex items-start gap-3">
            <BookOpen size={20} className="help-head-icon shrink-0 mt-0.5" />
            <div>
              <p className="eyebrow help-eyebrow mono">UNDERSTAND YOUR LAND</p>
              <h3 className="help-title">Help &amp; glossary</h3>
            </div>
          </div>
          <span
            role="button"
            tabIndex={0}
            aria-label="Close help"
            className="help-close"
            onClick={handleClose}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClose(); }}
          >
            <X size={18} />
          </span>
        </header>

        {onTakeTour && !lockOpen ? (
          <div className="help-tour-wrap">
            <button type="button" className="help-tour-btn" onClick={onTakeTour}>
              Take a tour of the app
            </button>
          </div>
        ) : null}

        {/* Search */}
        <div className="help-search-wrap" data-tour="help-search">
          <Search size={15} className="help-search-icon" />
          <input
            ref={searchRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search the glossary, e.g. EDX, NDVI, TNFD"
            className="help-search-input mono"
            aria-label="Search the glossary"
          />
        </div>

        {/* TOC (handoff 30 Item 2) — only when the scores primer is present */}
        {wide && !searching ? (
          <nav className="help-toc" aria-label="Help sections">
            <button type="button" className="help-toc-a" onClick={() => scrollToId('help-ref')}>On this page</button>
            <button type="button" className="help-toc-a" onClick={() => scrollToId('help-scores')}>How to read the scores</button>
            <button type="button" className="help-toc-a" onClick={() => scrollToId('glHead')}>Glossary</button>
          </nav>
        ) : null}

        {/* Scrollable body */}
        <div className="help-drawer-body" ref={bodyRef}>
          <div className="help-context" id="help-ref">
            <span className="eyebrow mono help-context-eyebrow">ON THIS PAGE</span>
            <p>{contextNote}</p>
          </div>

          {/* Reference block hides while searching (per signed-off RB UX). */}
          {!searching && referenceBlock}

          {/* Personalised primer + EDX/brittleness tables (handoff 30 Item 2). */}
          {!searching && siteContext ? <HowToReadScores site={siteContext} /> : null}

          {/* Term-help glossary (anchored ids for deep-link from Overview "?") */}
          <section className="help-block" id="glHead" data-tour="help-glossary">
            {!searching && <h4 className="help-block-title">Glossary</h4>}
            {searching && !hasResults && (
              <p className="help-empty">No matches for &ldquo;{query}&rdquo;.</p>
            )}
            {filteredTermHelp.length > 0 && (
              <div className="help-cat">
                {!searching && <h5 className="help-cat-title mono">On-screen terms</h5>}
                <dl className="help-terms">
                  {filteredTermHelp.map((t) => (
                    <div key={t.id} className="help-term" id={`gl-${t.id}`}>
                      <dt><Highlight text={t.term} query={searching ? query.trim() : ''} /></dt>
                      <dd><Highlight text={t.long || t.short} query={searching ? query.trim() : ''} /></dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
            {filtered.map((cat) => (
              <div key={cat.id} className="help-cat">
                <h5 className="help-cat-title mono">{cat.title}</h5>
                <dl className="help-terms">
                  {cat.terms.map((t) => (
                    <div key={t.term} className="help-term">
                      <dt><Highlight text={t.term} query={searching ? query.trim() : ''} /></dt>
                      <dd><Highlight text={t.def} query={searching ? query.trim() : ''} /></dd>
                    </div>
                  ))}
                </dl>
              </div>
            ))}
          </section>
        </div>
      </aside>
    </div>
  );
}
